import { useEffect, useState, useCallback, useMemo } from 'react';
import { Select } from '@etherisc/ui-kit';
import { getAgriculturalActivities } from '../../config/countries/index';

interface AgriculturalActivitiesProps {
  countryCode: string;
  primaryActivity: string;
  otherActivities: string[];
  onPrimaryChange: (activity: string) => void;
  onOtherChange: (activities: string[]) => void;
  disabled?: boolean;
  errors?: {
    primaryActivity?: string;
    otherActivities?: string;
  };
}

export function AgriculturalActivities({
  countryCode,
  primaryActivity,
  otherActivities,
  onPrimaryChange,
  onOtherChange,
  disabled = false,
  errors = {}
}: AgriculturalActivitiesProps) {
  const [activities, setActivities] = useState<string[]>([]);

  useEffect(() => {
    if (!countryCode) {
      setActivities([]);
      return;
    }
    
    const countryActivities = getAgriculturalActivities(countryCode);
    setActivities(countryActivities);
    
    if (primaryActivity && !countryActivities.includes(primaryActivity)) {
      onPrimaryChange('');
    }
    
    const otherActivitiesArray = Array.isArray(otherActivities) ? otherActivities : [];
    const validOtherActivities = otherActivitiesArray.filter(activity => 
      countryActivities.includes(activity)
    );
    if (validOtherActivities.length !== otherActivitiesArray.length) {
      onOtherChange(validOtherActivities);
    }
  }, [countryCode, primaryActivity, otherActivities, onPrimaryChange, onOtherChange]);

  const activityOptions = activities.map(activity => ({
    value: activity,
    label: activity,
  }));

  const secondaryOptions = useMemo(
    () => activities.filter(a => a !== primaryActivity),
    [activities, primaryActivity]
  );

  const toggleSecondary = useCallback((activity: string) => {
    const current = Array.isArray(otherActivities) ? otherActivities : [];
    if (current.includes(activity)) {
      onOtherChange(current.filter(a => a !== activity));
    } else {
      onOtherChange([...current, activity]);
    }
  }, [otherActivities, onOtherChange]);

  const isCountryMissing = !countryCode;

  return (
    <div className="space-y-4">
      {isCountryMissing && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            Please select a country first to see available agricultural activities
          </p>
        </div>
      )}
      
      <Select
        label="Primary Activity/Product"
        value={primaryActivity}
        onValueChange={onPrimaryChange}
        options={activityOptions}
        disabled={disabled || isCountryMissing}
        error={errors.primaryActivity}
        placeholder={isCountryMissing ? "Select country first" : "Select primary agricultural activity"}
      />

      {!isCountryMissing && secondaryOptions.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Secondary Activities
          </label>
          <div className="border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto space-y-1">
            {secondaryOptions.map(activity => {
              const checked = Array.isArray(otherActivities) && otherActivities.includes(activity);
              return (
                <label key={activity} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSecondary(activity)}
                    disabled={disabled}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  {activity}
                </label>
              );
            })}
          </div>
          {errors.otherActivities && (
            <p className="text-sm text-red-600 mt-1">{errors.otherActivities}</p>
          )}
        </div>
      )}
    </div>
  );
}
